import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { AssetType } from 'config/consts';
import { GetUserAssetResult, getPositions } from 'config/api';
import {
  PortfolioLoading,
  PortfolioRatioStuffingProps,
  PortfolioRatioStuffingTabProps,
  TypeKey,
  getRatio_,
  rawDataList,
} from './PortfolioRatioStuffing';
import { PortfolioRatioStuffingInternal } from './PortfolioRatioStuffingInternal';
import { PositionItemsProps } from '../ProfitAndLossStuffing/ProfitAndLossStuffing';
import { isHiddenProductTypes } from '../PortfolioStuffing/asset/PortfolioStuffingAsset';

const getProductsRowDataList = (data: GetUserAssetResult) => {
  return [
    {
      typeKey: AssetType['国内現物'],
      val: data.StockAmount,
    },
    {
      typeKey: AssetType['未上場株式'],
      val: data.UnlistedStockAmount,
    },
    {
      typeKey: AssetType['投資信託'],
      val: data.InvestmentTrustAmount,
    },
    {
      typeKey: AssetType['外国株式'],
      val: data.USStockAmount,
    },
    {
      typeKey: AssetType['国内ETF・ETN'],
      val: data.EtfEtnAmount,
    },
    {
      typeKey: AssetType['外国ETF・ETN'],
      val: data.ForeignEtfEtnAmount,
    },
    {
      typeKey: AssetType['国内REIT'],
      val: data.ReitAmount,
    },
    {
      typeKey: AssetType['外国REIT'],
      val: data.ForeignReitAmount,
    },
    {
      typeKey: AssetType['国内債券'],
      val: data.BondAmount,
    },
    {
      typeKey: AssetType['外国債券'],
      val: data.ForeignBondAmount,
    },
    {
      typeKey: AssetType['国内EB'],
      val: data.EbAmount,
    },
    {
      typeKey: AssetType['外国EB'],
      val: data.ForeignEbAmount,
    },
    {
      typeKey: AssetType['不動産'],
      val: data.RealEstateAmount,
    },
    {
      typeKey: AssetType['ロボアドバイザー'],
      val: data.RoboAdvisorAmount,
    },
    {
      typeKey: AssetType['信用'],
      val: data.MarginAmount,
    },
    {
      typeKey: AssetType['預金_MRF'],
      val: data.CashAmount,
    },
    {
      typeKey: AssetType['その他'],
      val: data.OtherAmount,
    },
  ] as rawDataList[];
};

export const PortfolioRatioStuffingProducts = ({
  asset,
  brokersInfo,
  UserBrokerUpdateTime,
  assetBundleType,
  toPreview,
  backHome,
  selectedType,
  setSelectedType,
  getIsOutOfDate,
}: PortfolioRatioStuffingProps &
  PortfolioRatioStuffingTabProps): React.ReactElement => {
  const { data, isFetching, isRefetching, refetch } = useQuery(
    'portfolioStuffingPositions',
    () => fetchPositions(assetBundleType.key),
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    (async () => {
      await refetch();
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetBundleType]);

  const getRatio = getRatio_(asset.TotalAssetAmount ?? 0);
  const getDatum = ({
    typeKey,
    val,
  }: {
    typeKey: TypeKey;
    val: number | null;
  }): {
    typeKey: TypeKey;
    ratio: number;
    amount: number;
  }[] => {
    if (val === null) return [];
    return [{ typeKey, amount: val, ratio: getRatio(val) }];
  };

  const list = getProductsRowDataList(asset);
  const rawData = list.flatMap(item => getDatum(item));

  if (data === null || data === undefined || (isFetching && isRefetching))
    return <PortfolioLoading type={'products'} />;

  const positions_ = data as PositionItemsProps[];
  const positions = positions_.filter(
    position => !isHiddenProductTypes(position.productType)
  );

  const summaryPositions = positions.reduce((acc, v): PositionItemsProps[] => {
    const last = acc[acc.length - 1];

    const isSameSymbol =
      last &&
      last.brokerID &&
      last.symbol === v.symbol &&
      last.symbolName === v.symbolName;

    if (isSameSymbol)
      return [
        ...(acc.slice(0, acc.length - 1) as PositionItemsProps[]),
        {
          ...last,
          currentAmount: (last.currentAmount ?? 0) + (v.currentAmount ?? 0),
          profitOrLoss: (last.profitOrLoss ?? 0) + (v.profitOrLoss ?? 0),
          qty: (last.qty ?? 0) + (v.qty ?? 0),
        },
      ];
    return [...acc, v];
  }, [] as PositionItemsProps[]);

  const rawDataPositions = summaryPositions.map(position => {
    const isOutOfDate = getIsOutOfDate(position)
      ? { isOutOfDate: true, account: position.account }
      : null;

    return { ...position, refresh: isOutOfDate };
  });

  return (
    <PortfolioRatioStuffingInternal
      {...{
        asset,
        brokersInfo,
        UserBrokerUpdateTime,
        assetBundleType,
        toPreview,
        backHome,
        selectedType,
        setSelectedType,
        rawData,
        rawDataPositions,
        isFetching,
        isRefetching,
      }}
    />
  );
};

const fetchPositions = async (key: number) => {
  const groupID = key !== 0 && key !== 9999 ? key.toString() : undefined;

  const res_ = await getPositions(groupID);
  const res = res_.Items.map(res => {
    return {
      brokerID: res.BrokerID,
      account: res.Account,
      symbol: res.Symbol,
      symbolName: res.SymbolName,
      accountType: res.AccountType,
      productType: res.ProductType,
      cashMargin: res.CashMargin,
      side: res.Side,
      qty: res.Qty,
      price: res.Price,
      currentAmount: res.CurrentAmount,
      currentAmountUSD: res.CurrentAmountUSD,
      profitOrLoss: res.ProfitOrLoss,
      profitOrLossUSD: res.ProfitOrLossUSD,
      validationAmount: res.ValidationAmount,
      diffProfitOrLoss: res.DiffProfitOrLoss,
      diffProfitOrLossUSD: res.DiffProfitOrLossUSD,
      currencyUnit: res.CurrencyUnit,
    };
  });
  return res;
};
